import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";
import {Stack} from "@mui/material";
import {styled} from "@mui/material/styles";

import CustomButton from "components/CustomButton";
import {ReactComponent as SparkleIcon} from "assets/img/sparkle.svg";
import {black500, blue25, blue100, blue500, greyishBlue600} from "assets/jss/main";

MassActionBar.propTypes = {
    titleId: PropTypes.string.isRequired,
    selectedCount: PropTypes.number.isRequired,
    onCancel: PropTypes.func.isRequired,
    actions: PropTypes.arrayOf(PropTypes.shape({
        labelId: PropTypes.string.isRequired,
        color: PropTypes.string,
        onClick: PropTypes.func.isRequired,
    })).isRequired,
    loading: PropTypes.bool,
    anchorRef: PropTypes.shape({current: PropTypes.any}).isRequired,
};

const Bar = styled('div')({
    position: 'fixed',
    bottom: '16px',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    border: `1px solid ${blue100}`,
    borderRadius: '16px',
    backgroundColor: blue25,
    boxShadow: '0px 0px 22px 0px rgba(0, 0, 0, 0.08)',
});

const Title = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: black500,
    fontSize: '20px',
    fontWeight: 600,
    '& svg': {
        width: '16px',
        height: '16px',
        color: blue500,
    },
});

const Details = styled('span')({
    color: greyishBlue600,
    fontSize: '14px',
    fontWeight: 600,
});

const CancelLink = styled('button')({
    padding: 0,
    border: 'none',
    background: 'none',
    color: blue500,
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
});

function MassActionBar({titleId, selectedCount, onCancel, actions, loading = false, anchorRef}) {
    const [frame, setFrame] = useState(null);
    const visible = selectedCount > 0;

    useEffect(() => {
        if (!visible) {
            return undefined;
        }

        const place = () => {
            const rect = anchorRef.current?.getBoundingClientRect();
            setFrame(rect ? {left: rect.left + 16, width: rect.width - 32} : null);
        };
        place();
        window.addEventListener('resize', place);

        return () => window.removeEventListener('resize', place);
    }, [visible, anchorRef]);

    if (!visible || null === frame) {
        return null;
    }

    return (
        <Bar style={frame}>
            <Stack direction="row" alignItems="center" gap="8px">
                <Title><SparkleIcon/><FormattedMessage id={titleId}/></Title>
                <Details>• <FormattedMessage id="enrichment.selection.count" values={{count: selectedCount}}/></Details>
                <CancelLink type="button" onClick={onCancel}>
                    <FormattedMessage id="enrichment.selection.cancel"/>
                </CancelLink>
            </Stack>
            <Stack direction="row" gap="12px">
                {actions.map((action) => (
                    <CustomButton key={action.labelId} variant="contained" color={action.color ?? 'blue'} loading={loading} onClick={action.onClick}>
                        <FormattedMessage id={action.labelId}/>
                    </CustomButton>
                ))}
            </Stack>
        </Bar>
    );
}

export default MassActionBar;
